import Popover, { type TPopoverProps } from '@atlas/uicorn/lib/Display/Popover';
import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import cn from 'classnames';
import styles from './Tooltip.module.css';

const Tooltip = React.memo(function Tooltip(props: TTooltipProps): React.ReactElement {
    const { className, children, placement = 'top', ...elementProps } = props;

    return (
        <Popover
            {...elementProps}
            trigger="hover"
            placement={placement}
            className={cn(styles.tooltip, className)}
            noFocusOnClose
        >
            {children}
        </Popover>
    );
});

export default Tooltip;

// #region Types

export type TTooltipProps = Omit<TPopoverProps, 'trigger'>;

// #endregion

// #region Examples

export const Example: TExample = {
    position: 'page',
    containerStyle: {
        padding: 250,
        height: '200%',
        width: '200%',
        alignItems: 'flex-start',
    },
    render(props) {
        const { overflowed } = props;

        const content = overflowed ? (
            <>
                Lorem ipsum dolor sit amet consectetur adipisicing elit. Consequatur repellat perferendis unde eum
                autem. Quidem fuga perferendis odit recusandae numquam eius eligendi, ducimus corrupti totam
                perspiciatis id amet consequuntur rem expedita? Dolores veritatis distinctio maxime eaque hic, molestiae
                ipsum excepturi pariatur nobis iure nam qui repellat labore minus totam quo?
            </>
        ) : (
            <>
                Refresh page <kbd>⌘R</kbd>
            </>
        );

        return (
            <Tooltip
                placement={props.placement}
                open={props.open}
                content={content}
                onOpenChange={props.onOpenChange}
                onOpen={props.onOpen}
                onClose={props.onClose}
            >
                <button>Hover to trigger</button>
            </Tooltip>
        );
    },
    controls: {
        placement: [
            'top',
            'right',
            'bottom',
            'left',
            'left-end',
            'left-start',
            'right-end',
            'right-start',
            'bottom-end',
            'bottom-start',
            'top-end',
            'top-start',
        ],
        open: { type: 'boolean', initial: true, connected: true },
        overflowed: { type: 'boolean', initial: false, connected: true },
    },
    handlers: {
        onOpenChange: 'console',
        onOpen: 'console',
        onClose: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=dl6EllQ4tm9YKdhg-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=dl6EllQ4tm9YKdhg-0',
    ],
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Tooltip', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
